import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { vehicleRequestsApi, vehiclesApi } from '../../api/fleet';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';
import { VEHICLE_REQUEST_STATUS_LABELS } from './VehicleRequestsListPage';
import { VEHICLE_REQUEST_STAGES } from '../../config/workflow-stages';
import { formatDate } from '../../utils/formatDate';

export function VehicleRequestWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const vehicleRequestId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: requestData, isError } = useQuery({ queryKey: ['vehicle-request', vehicleRequestId], queryFn: () => vehicleRequestsApi.get(vehicleRequestId) });
  const { data: docsData } = useQuery({ queryKey: ['vehicle-request-documents', vehicleRequestId], queryFn: () => genericDocumentApi.listFor('vehicle_request', vehicleRequestId) });
  const vehicleRequest = requestData?.data;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['vehicle-request', vehicleRequestId] });

  const { data: availableVehiclesData } = useQuery({ queryKey: ['vehicles', 'available'], queryFn: () => vehiclesApi.list({ status: 'available' }) });
  const availableVehicles = availableVehiclesData?.data ?? [];

  const submitMutation = useMutation({
    mutationFn: () => vehicleRequestsApi.submit(vehicleRequestId),
    onSuccess: () => { showSuccess('Vehicle request submitted.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });
  const approveMutation = useMutation({
    mutationFn: () => vehicleRequestsApi.approve(vehicleRequestId),
    onSuccess: () => { showSuccess('Vehicle request approved.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });
  const { register: registerReject, handleSubmit: handleRejectSubmit, reset: resetReject } = useForm<{ reason: string }>();
  const rejectMutation = useMutation({
    mutationFn: (reason: string) => vehicleRequestsApi.reject(vehicleRequestId, reason),
    onSuccess: () => { showSuccess('Vehicle request rejected.'); resetReject(); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const [assignForm, setAssignForm] = useState({ vehicleId: '', driverEmployeeId: '' });
  const assignMutation = useMutation({
    mutationFn: () => vehicleRequestsApi.assign(vehicleRequestId, { vehicleId: Number(assignForm.vehicleId), driverEmployeeId: Number(assignForm.driverEmployeeId) }),
    onSuccess: () => { showSuccess('Vehicle and driver assigned.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  if (isError) return <div className="page"><div className="error-state">Failed to load this vehicle request. Please try refreshing the page.</div></div>;
  if (!vehicleRequest) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  return (
    <div className="page">
      <RecordHeader
        title={vehicleRequest.request_reference}
        readOnly={false}
        entityType="vehicle_request"
        entityId={vehicleRequestId}
        fields={[
          { label: 'Route', value: `${vehicleRequest.pickup_location} → ${vehicleRequest.destination}` },
          { label: 'Departure', value: formatDate(vehicleRequest.departure_date) },
          { label: 'Return', value: formatDate(vehicleRequest.expected_return_date) },
          { label: 'Status', value: <StatusBadge status={vehicleRequest.status} label={VEHICLE_REQUEST_STATUS_LABELS[vehicleRequest.status]} /> },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'overview', label: 'Overview', content: (
              <div>
                <SectionHeader title="Details" />
                <dl className="detail-grid">
                  <dt>Purpose</dt><dd>{vehicleRequest.purpose}</dd>
                  <dt>Passengers</dt><dd>{vehicleRequest.number_of_passengers ?? '-'}</dd>
                  <dt>Priority</dt><dd>{vehicleRequest.priority}</dd>
                  <dt>Assigned Vehicle</dt><dd>{vehicleRequest.assigned_vehicle_id ?? 'Not yet assigned'}</dd>
                </dl>
                {vehicleRequest.status === 'draft' && <button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>Submit for Approval</button>}
              </div>
            ),
          },
          {
            key: 'approval', label: 'Approval', content: (
              <div>
                <SectionHeader title="Approval" />
                {['submitted', 'under_review'].includes(vehicleRequest.status) && (
                  <>
                    <button onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending}>Approve</button>
                    <form className="inline-form" onSubmit={handleRejectSubmit((d) => rejectMutation.mutate(d.reason))}>
                      <input placeholder="Reason for rejection" {...registerReject('reason', { required: true })} />
                      <button type="submit" disabled={rejectMutation.isPending}>Reject</button>
                    </form>
                  </>
                )}
              </div>
            ),
          },
          {
            key: 'assignment', label: 'Vehicle & Driver', content: (
              <div>
                <SectionHeader title="Assign Vehicle and Driver" />
                {vehicleRequest.status === 'approved' && !vehicleRequest.assigned_vehicle_id ? (
                  <form className="inline-form" onSubmit={(e) => { e.preventDefault(); assignMutation.mutate(); }}>
                    <select value={assignForm.vehicleId} onChange={(e) => setAssignForm((s) => ({ ...s, vehicleId: e.target.value }))} required>
                      <option value="">Select vehicle…</option>
                      {availableVehicles.map((v) => <option key={v.id} value={v.id}>{v.registration_number} - {v.make} {v.model}</option>)}
                    </select>
                    <EmployeePicker value={assignForm.driverEmployeeId} onChange={(e) => setAssignForm((s) => ({ ...s, driverEmployeeId: e.target.value }))} />
                    <button type="submit" disabled={assignMutation.isPending}>Assign</button>
                  </form>
                ) : vehicleRequest.assigned_vehicle_id ? (
                  <p>Vehicle #{vehicleRequest.assigned_vehicle_id} and driver #{vehicleRequest.assigned_driver_employee_id} assigned.</p>
                ) : (
                  <p className="muted">Assignment is available once the request is approved.</p>
                )}
              </div>
            ),
          },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsData?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('vehicle_request', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('vehicle_request', vehicleRequestId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('vehicle_request', documentId)}
                queryKeyToInvalidate={['vehicle-request-documents', vehicleRequestId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking', label: 'Tracking', content: (
              <div>
                <WorkflowTrackingTab entityType="vehicle_request" entityId={vehicleRequestId} actionStageMap={VEHICLE_REQUEST_STAGES} statusLabels={VEHICLE_REQUEST_STATUS_LABELS} />
                <CommentsThread entityType="vehicle_request" entityId={vehicleRequestId} section="fleet" heading="Comments for this Vehicle Request" />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
